import './FellowshipUnseenEggReveal.css';

import { useState, useEffect, useCallback, useRef } from 'react';
import eggLarge from '../assets/images/egg_large.png';
import eggSplat from '../assets/images/egg_splat.png';
import { URL } from './constants';

/**
 * @param {string[]} names
 */
function formatEggers(names) {
  const n = names.length;
  if (n === 0) return '';
  if (n === 1) return names[0];
  if (n === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[n - 1]}`;
}

/**
 * @param {{ egg_id: number, egger: string, day?: string|null }[]} unseen
 * @returns {{ egg_id: number, egger: string, day?: string|null }[][]}
 */
export function buildUnseenEggBatches(unseen) {
  if (!Array.isArray(unseen) || unseen.length === 0) return [];
  const batches = [];
  if (unseen[0]) batches.push([unseen[0]]);
  if (unseen[1]) batches.push([unseen[1]]);
  if (unseen.length >= 3) {
    batches.push(unseen.slice(2));
  }
  return batches;
}

/** Spin phase length (ms) before switching to splat. */
const EGG_SPIN_MS = 2_500;

/** Splat fade length (ms); applied inline on the splat wrapper so it stays in sync with the Safari fallback timer. */
const EGG_SPLAT_ANIM_MS = 4_500;

/**
 * One batch: spin large egg, then splat fades, then onComplete(batch).
 * @param {{ batch: { egg_id: number, egger: string }[], onComplete: (b: { egg_id: number, egger: string }[]) => void }} props
 */
function EggBatchReveal({ batch, onComplete }) {
  const [stage, setStage] = useState('spin');
  const finishedRef = useRef(false);

  useEffect(() => {
    finishedRef.current = false;
    setStage('spin');
    const id = window.setTimeout(() => setStage('splat'), EGG_SPIN_MS);
    return () => window.clearTimeout(id);
  }, [batch]);

  useEffect(() => {
    if (stage !== 'splat') return;
    const id = window.setTimeout(() => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      onComplete(batch);
    }, EGG_SPLAT_ANIM_MS + 400);
    return () => window.clearTimeout(id);
  }, [stage, batch, onComplete]);

  const title = `You've been egged by ${formatEggers(batch.map((e) => e.egger))}`;

  const onSplatAnimationEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (finishedRef.current) return;
    finishedRef.current = true;
    onComplete(batch);
  };

  return (
    <div
      className="fellowship-unseen-egg-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-live="assertive"
      aria-label={title}
    >
      <div className="fellowship-unseen-egg-card">
        <p className="fellowship-unseen-egg-title">{title}</p>
        <div className="fellowship-unseen-egg-img-wrap">
          <div
            className={`fellowship-unseen-egg-phase ${
              stage === 'spin'
                ? 'fellowship-unseen-egg-phase--spin-visible'
                : 'fellowship-unseen-egg-phase--off'
            }`}
            aria-hidden={stage !== 'spin'}
          >
            <div className="fellowship-unseen-egg-spin-wrap">
              <img
                src={eggLarge}
                alt=""
                className="fellowship-unseen-egg-large"
                decoding="async"
              />
            </div>
          </div>
          <div
            className={`fellowship-unseen-egg-phase ${
              stage === 'splat'
                ? 'fellowship-unseen-egg-phase--splat-visible fellowship-unseen-egg-splat-wrap'
                : 'fellowship-unseen-egg-phase--preload'
            }`}
            style={
              stage === 'splat'
                ? {
                    WebkitAnimationDuration: `${EGG_SPLAT_ANIM_MS}ms`,
                    animationDuration: `${EGG_SPLAT_ANIM_MS}ms`,
                  }
                : undefined
            }
            onAnimationEnd={stage === 'splat' ? onSplatAnimationEnd : undefined}
            aria-hidden={stage !== 'splat'}
          >
            <img
              src={eggSplat}
              alt=""
              className="fellowship-unseen-egg-splat-img"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ batches: { egg_id: number, egger: string }[][], onAllComplete?: () => void }} props
 */
export default function FellowshipUnseenEggReveal({ batches, onAllComplete }) {
  const [index, setIndex] = useState(0);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (notifiedRef.current) return;
    if (!batches.length || index >= batches.length) {
      notifiedRef.current = true;
      onAllComplete?.();
    }
  }, [batches, index, onAllComplete]);

  const handleBatchComplete = useCallback(async (completedBatch) => {
    if (!completedBatch?.length) {
      setIndex((i) => i + 1);
      return;
    }
    try {
      await Promise.all(
        completedBatch.map((egg) =>
          fetch(`${URL}/view_egg`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ egg_id: egg.egg_id }),
          }).then((res) => {
            if (!res.ok) {
              console.error('view_egg failed', res.status);
            }
          })
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIndex((i) => i + 1);
    }
  }, []);

  if (!batches.length || index >= batches.length) return null;

  return (
    <EggBatchReveal
      key={index}
      batch={batches[index]}
      onComplete={handleBatchComplete}
    />
  );
}
